import React, { PureComponent } from 'react'
import { css } from '@emotion/react'
import cn from 'classnames'
import ResizeObserver from 'resize-observer-polyfill'
import { Tooltip } from 'shared/ui'
import { debounce, propFunc } from 'util/functions'
import { pluralize } from 'util/strings'
import { memoize } from 'util/memoization'

const labelStyleColor = color => css`
  &&&& {
    color: ${color};
  }
`

const labelStyleBackgroundColor = color => css`
  &&&& {
    background-color: ${color}26;
  }
`

export default class LabelBox extends PureComponent {
  constructor(props) {
    super(props)
    this.labelContainer = React.createRef()
    this.resizeTimeout = null
  }

  state = { isVerticalOverflow: false, isHorizontalOverflow: false }

  componentDidMount() {
    this.handleResize()
    this.observer = new ResizeObserver(this.handleResize)
    const labelContainer = this.labelContainer && this.labelContainer.current
    this.observer.observe(labelContainer)
  }

  componentWillUnmount() {
    this.removeObserver()
  }

  getLabels = memoize(labelsJSON => JSON.parse(labelsJSON))

  handleResize = debounce(() => {
    if (this.isUnmounting) return

    const labelContainer = this.labelContainer && this.labelContainer.current
    if (!labelContainer) return

    const isVerticalOverflow = labelContainer.scrollHeight > 24
    // happens when the last visible tag is too wide to fit into conversation list pane
    const isHorizontalOverflow =
      labelContainer.scrollWidth > labelContainer.clientWidth

    if (
      isVerticalOverflow !== this.state.isVerticalOverflow ||
      isHorizontalOverflow !== this.state.isHorizontalOverflow
    ) {
      this.setState({ isVerticalOverflow, isHorizontalOverflow })
    }
  }, 100)

  removeObserver() {
    this.isUnmounting = true
    this.observer.disconnect()
    this.observer = null
  }

  render() {
    const { labelsJSON, labelIds } = this.props
    const { isVerticalOverflow, isHorizontalOverflow } = this.state
    const labels = this.getLabels(labelsJSON)

    return (
      <div className="labelbox">
        <div className="labels" ref={this.labelContainer}>
          <div className={cn('labels-inner', { hidden: isHorizontalOverflow })}>
            {labels.map(({ name, color }) => {
              return (
                <span
                  key={name}
                  className="label"
                  css={[
                    propFunc(labelStyleColor, color),
                    propFunc(labelStyleBackgroundColor, color),
                  ]}
                >
                  {name}
                </span>
              )
            })}
          </div>
        </div>
        {(isVerticalOverflow || isHorizontalOverflow) && (
          <Tooltip
            className="tooltip"
            position="bottom"
            tooltip={labels.map(({ name, color }) => {
              return (
                <span key={`${name}-tooltip`} css={labelStyleColor(color)}>
                  {name}
                  <br />
                </span>
              )
            })}
          >
            <span className="more">
              +{' '}
              {isHorizontalOverflow
                ? `${labelIds.length} ${pluralize(labelIds.length, 'Tag')}`
                : 'more'}
            </span>
          </Tooltip>
        )}
      </div>
    )
  }
}
